import axios from "axios";
import { toast } from "sonner";
import type { FieldValues, Path, UseFormReturn, UseFormSetError } from "react-hook-form";

/**
 * Maps API error responses onto react-hook-form's setError.
 *
 * Handles:
 * - 409 Conflict with `detail` string containing field keywords
 * - Validation errors with `errors` object mapping field names to messages
 *
 * @param error - The caught error (typically from axios)
 * @param setError - react-hook-form's setError function
 * @param fieldMap - Maps API keywords/field names to form field paths
 * @returns null if errors were set on specific fields, or a fallback message string
 */
export function setApiErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fieldMap?: Record<string, Path<T>>
): string | null {
  if (!axios.isAxiosError(error)) {
    return "An unexpected error occurred. Please try again.";
  }

  const status = error.response?.status;
  const data = error.response?.data;

  // 409 Conflict — check detail string for field keywords
  if (status === 409 && data?.detail && fieldMap) {
    const detail = String(data.detail);
    for (const [keyword, fieldPath] of Object.entries(fieldMap)) {
      if (detail.includes(keyword)) {
        setError(fieldPath, {
          type: "server",
          message: `This ${fieldPath} is already taken. Please choose another.`,
        });
        return null;
      }
    }
    // 409 but no matching field
    return data.detail;
  }

  // Validation errors object — { errors: { FieldName: ["msg1", "msg2"] } }
  if (data?.errors && typeof data.errors === "object" && fieldMap) {
    let anyFieldSet = false;
    for (const [apiField, messages] of Object.entries(data.errors)) {
      const fieldPath = fieldMap[apiField];
      if (fieldPath) {
        const msg = Array.isArray(messages) ? messages.join("; ") : String(messages);
        setError(fieldPath, { type: "server", message: msg });
        anyFieldSet = true;
      }
    }
    if (anyFieldSet) return null;
  }

  // Generic status-based messages
  if (status === 409) {
    return "A conflict occurred. Please check your input.";
  }
  if (status && status >= 500) {
    return "Service is temporarily unavailable. Please try again.";
  }

  return data?.title ?? data?.message ?? "An unexpected error occurred. Please try again.";
}

/**
 * Counts validation errors by reading the rendered DOM. More reliable than
 * traversing react-hook-form's nested error object (zod refinements on
 * objects/arrays can place messages at unexpected paths) and guarantees the
 * count matches what the user actually sees.
 *
 * Scoped to the first <form> element on the page.
 */
function collectRenderedErrors(): HTMLElement[] {
  const formEl = document.querySelector("form");
  return formEl
    ? Array.from(formEl.querySelectorAll<HTMLElement>("[data-field-error]"))
    : [];
}

export function buildValidationMessage(count: number): string {
  return count === 1
    ? "Please fix the validation error before submitting"
    : `Please fix ${count} validation errors before submitting`;
}

/**
 * Scroll the error into view by targeting the nearest `overflow-y: auto|scroll`
 * ancestor directly. Avoids `scrollIntoView`'s cascade behavior, which in
 * Chrome scrolls ancestor elements even when they have `overflow: hidden`
 * (if scrollHeight > clientHeight) — that pushes sticky headers and action
 * bars offscreen on repeated submits.
 *
 * Honors `scroll-mt-20` (scroll-margin-top: 5rem = 80px) on FieldError.
 */
function scrollToErrorElement(errorEl: HTMLElement) {
  let container: HTMLElement | null = errorEl.parentElement;
  while (container) {
    const overflowY = window.getComputedStyle(container).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") break;
    container = container.parentElement;
  }
  if (!container) {
    errorEl.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  const containerRect = container.getBoundingClientRect();
  const errorRect = errorEl.getBoundingClientRect();
  const offset = errorRect.top - containerRect.top - 80;
  container.scrollTo({ top: container.scrollTop + offset, behavior: "smooth" });
}

/**
 * Extracts human-readable messages from react-hook-form's nested errors
 * object. Used as a fallback when validation fails but no FieldError
 * components are rendered in the DOM (e.g. conditionally-hidden fields).
 */
function extractFormErrorMessages(errors: Record<string, any>): string[] {
  const messages: string[] = [];
  for (const value of Object.values(errors)) {
    if (value?.message) {
      messages.push(String(value.message));
    } else if (typeof value === "object" && value !== null) {
      messages.push(...extractFormErrorMessages(value as Record<string, any>));
    }
  }
  return messages;
}

/**
 * Waits one animation frame for react-hook-form's error re-render to commit,
 * then counts rendered FieldErrors, toasts the count, and scrolls the first
 * into view (just below the sticky action bar).
 *
 * If validation errors exist but no FieldError components rendered (e.g.
 * the failing field is conditionally hidden), falls back to extracting
 * messages from form state so the user always gets feedback.
 *
 * Returns the count of surfaced errors (zero if neither path produced any).
 */
export function reportAndScrollToFormErrors<T extends FieldValues>(
  form: UseFormReturn<T>,
): Promise<number> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      const errorEls = collectRenderedErrors();
      const count = errorEls.length;
      if (count > 0) {
        toast.error(buildValidationMessage(count));
        scrollToErrorElement(errorEls[0]);
        resolve(count);
        return;
      }

      const messages = extractFormErrorMessages(
        form.formState.errors as Record<string, any>,
      );
      if (messages.length > 0) {
        toast.error(buildValidationMessage(messages.length), {
          description: messages.length === 1
            ? messages[0]
            : `${messages[0]} (and ${messages.length - 1} more)`,
          duration: 8000,
        });
        resolve(messages.length);
        return;
      }

      resolve(0);
    });
  });
}
